import React, { useEffect, useRef, useState } from 'react';
import { confirmAction } from '../utils/confirm';
import { useNavigate } from 'react-router-dom';
import { fetchDevices, addDevice, enablePairingMode, disablePairingMode, updateDevice, deleteDevice, deleteDeviceData, setDeviceDisabled, fetchPendingDevices, confirmPendingDevice, dismissPendingDevice, replaceDevice, fetchCoordinatorStatus } from '../services/api';
import { fetchGateways, GatewayRecord } from '../services/gatewaysApi';
import { Device } from '../types';
import { PendingDevice } from '../types/api';
import { getStatusColor, getDeviceIcon, getStatusLabel, getDeviceTypeLabel } from '../styles/shared';
import ErrorBanner from './shared/ErrorBanner';
import SignalIcon from './shared/SignalIcon';
import BatteryIcon from './shared/BatteryIcon';
import { useAuth } from '../context/AuthContext';
import EditDeviceForm from './device-list/EditDeviceForm';
import PairTab from './device-list/PairTab';
import ManualTab from './device-list/ManualTab';

const DeviceList: React.FC = () => {
    const { isReadOnly } = useAuth();
    const [devices, setDevices] = useState<Device[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [showEditForm, setShowEditForm] = useState<boolean>(false);
    const [pairingMode, setPairingMode] = useState<boolean>(false);
    const [pairingCountdown, setPairingCountdown] = useState<number>(0);
    const pairingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const pairingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [coordinatorConnected, setCoordinatorConnected] = useState<boolean>(false);
    const [zigbeeGateways, setZigbeeGateways] = useState<GatewayRecord[]>([]);
    const [pairingCoordinatorId, setPairingCoordinatorId] = useState<string>('');
    const [pendingDevices, setPendingDevices] = useState<PendingDevice[]>([]);
    const [confirmForms, setConfirmForms] = useState<Record<string, { name: string; type: string }>>({});
    const [replaceMode, setReplaceMode] = useState<Record<string, boolean>>({});
    const [replaceTarget, setReplaceTarget] = useState<Record<string, string>>({});
    const [newDevice, setNewDevice] = useState({
        name: '',
        type: 'light',
        status: 'online' as 'online' | 'offline' | 'error',
        ip_address: '',
        port: ''
    });
    const [editingDevice, setEditingDevice] = useState<Device | null>(null);
    const [filterText, setFilterText] = useState<string>('');
    const [filterStatus, setFilterStatus] = useState<'all' | 'online' | 'offline' | 'error'>('all');
    const [filterType, setFilterType] = useState<string>('all');
    const [filterCoordinator, setFilterCoordinator] = useState<string>('all');
    const [viewTab, setViewTab] = useState<'list' | 'pair' | 'manual'>('list');
    const navigate = useNavigate();

    const loadDevices = async () => {
        try {
            setLoading(true);
            const deviceData = await fetchDevices();
            setDevices(deviceData);
            setError(null);
        } catch (err: any) {
            const errorMessage = err.message || 'Kunde inte hämta enheter. Kontrollera anslutningen.';
            setError(errorMessage);
            console.error('Load devices error:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadDevices();
        fetchCoordinatorStatus().then(setCoordinatorConnected);
        fetchGateways().then(gws => setZigbeeGateways(gws.filter(gw => gw.zigbeeEnabled)));
    }, []);

    // Countdown timer for pairing mode
    useEffect(() => {
        if (pairingCountdown > 0) {
            const timer = setTimeout(() => {
                setPairingCountdown(pairingCountdown - 1);
            }, 1000);
            return () => clearTimeout(timer);
        } else if (pairingMode && pairingCountdown === 0) {
            setPairingMode(false);
        }
    }, [pairingCountdown, pairingMode]);

    const handleEnablePairing = async () => {
        setError(null);
        setViewTab('pair');
        try {
            const result = await enablePairingMode(254, pairingCoordinatorId || undefined); // 254 seconds (max for Zigbee)
            setPairingMode(true);
            setPairingCountdown(result.duration);
            // Only poll pending devices during pairing — NOT the full device list.
            // Calling loadDevices() here causes a full re-render which steals focus from the name input.
            // The main list is refreshed once when pairing ends (handleStopPairing / timeout).
            const interval = setInterval(async () => {
                try {
                    const pending = await fetchPendingDevices();
                    setPendingDevices(pending);
                    // Pre-fill confirm forms for any new pending devices
                    setConfirmForms(prev => {
                        const updated = { ...prev };
                        pending.forEach((d: any) => {
                            if (!updated[d.ieee_address]) {
                                // Pre-fill with existing name/type for previously registered devices
                                updated[d.ieee_address] = {
                                    name: d.name || '',
                                    type: d.type || 'temperature'
                                };
                            }
                        });
                        return updated;
                    });
                } catch (e) { /* ignore */ }
            }, 3000);
            pairingIntervalRef.current = interval;
            pairingTimeoutRef.current = setTimeout(() => { clearInterval(interval); setPairingMode(false); loadDevices(); }, result.duration * 1000);
        } catch (err: any) {
            const errorMessage = err.message || 'Kunde inte starta parkopplingsläget';
            setError(errorMessage);
            console.error('Enable pairing error:', err);
        }
    };

    const handleStopPairing = async () => {
        if (pairingIntervalRef.current) { clearInterval(pairingIntervalRef.current); pairingIntervalRef.current = null; }
        if (pairingTimeoutRef.current) { clearTimeout(pairingTimeoutRef.current); pairingTimeoutRef.current = null; }
        setPairingMode(false);
        setPairingCountdown(0);
        await disablePairingMode(pairingCoordinatorId || undefined);
        loadDevices();
    };

    const handleConfirmDevice = async (ieeeAddr: string) => {
        const form = confirmForms[ieeeAddr];
        if (!form?.name.trim()) {
            setError('Skriv ett namn för enheten');
            return;
        }
        try {
            await confirmPendingDevice(ieeeAddr, form.name.trim(), form.type);
            setPendingDevices(prev => prev.filter(d => d.ieee_address !== ieeeAddr));
            setConfirmForms(prev => { const n = { ...prev }; delete n[ieeeAddr]; return n; });
            await loadDevices();
        } catch (err: any) {
            setError(err.message || 'Kunde inte bekräfta enheten');
        }
    };

    const handleDismissPending = async (ieeeAddr: string) => {
        try {
            await dismissPendingDevice(ieeeAddr);
            setPendingDevices(prev => prev.filter(d => d.ieee_address !== ieeeAddr));
            setConfirmForms(prev => { const n = { ...prev }; delete n[ieeeAddr]; return n; });
            setReplaceMode(prev => { const n = { ...prev }; delete n[ieeeAddr]; return n; });
            setReplaceTarget(prev => { const n = { ...prev }; delete n[ieeeAddr]; return n; });
        } catch (err: any) {
            setError(err.message || 'Kunde inte avvisa enheten');
        }
    };

    const handleReplaceDevice = async (pendingIeeeAddr: string) => {
        const targetId = replaceTarget[pendingIeeeAddr];
        if (!targetId) {
            setError('Välj en enhet att ersätta');
            return;
        }
        try {
            await replaceDevice(pendingIeeeAddr, targetId);
            setPendingDevices(prev => prev.filter(d => d.ieee_address !== pendingIeeeAddr));
            setConfirmForms(prev => { const n = { ...prev }; delete n[pendingIeeeAddr]; return n; });
            setReplaceMode(prev => { const n = { ...prev }; delete n[pendingIeeeAddr]; return n; });
            setReplaceTarget(prev => { const n = { ...prev }; delete n[pendingIeeeAddr]; return n; });
            await loadDevices();
        } catch (err: any) {
            setError(err.message || 'Kunde inte ersätta enheten');
        }
    };

    const handleAddDevice = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        try {
            await addDevice({
                ...newDevice,
                ip_address: newDevice.ip_address || undefined,
                port: newDevice.port ? Number(newDevice.port) : undefined
            } as any);
            setNewDevice({ name: '', type: 'light', status: 'online', ip_address: '', port: '' });
            await loadDevices();
        } catch (err: any) {
            const errorMessage = err.message || 'Kunde inte lägga till enheten. Kontrollera uppgifterna och försök igen.';
            setError(errorMessage);
            console.error('Add device error:', err);
        }
    };;

    const handleDeviceClick = (deviceId: string) => {
        navigate(`/device-control?deviceId=${deviceId}`);
    };

    const handleEditDevice = (device: Device) => {
        setEditingDevice(device);
        setShowEditForm(true);
        setViewTab('list');
    };

    const handleUpdateDevice = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingDevice) return;
        
        setError(null);
        try {
            // Update device metadata
            await updateDevice(editingDevice.id, {
                name: editingDevice.name,
                type: editingDevice.type,
                battery_type: editingDevice.battery_type ?? null,
            });
            
            // Update device settings if present
            if (editingDevice.settings && Object.keys(editingDevice.settings).length > 0) {
                const { updateDeviceSettings } = await import('../services/zigbeeApi');
                await updateDeviceSettings(editingDevice.id, editingDevice.settings);
            }
            
            setShowEditForm(false);
            setEditingDevice(null);
            await loadDevices();
        } catch (err: any) {
            const errorMessage = err.message || 'Kunde inte spara enheten';
            setError(errorMessage);
            console.error('Update device error:', err);
        }
    };

    const handleRemoveDevice = async (device: Device) => {
        const confirmed = confirmAction(
            `Vill du ta bort "${device.name}" (${device.id})?\n\nEnheten kopplas bort från Zigbee-nätet och döljs överallt.\nHistoriken sparas för fakturering och rapporter.\n\nParkopplar du samma enhet igen kommer den tillbaka.`
        );
        
        if (!confirmed) return;
        
        setError(null);
        try {
            await deleteDevice(device.id);
            await loadDevices();
        } catch (err: any) {
            const errorMessage = err.message || 'Kunde inte ta bort enheten';
            setError(errorMessage);
            console.error('Remove device error:', err);
        }
    };

    const handleRemoveData = async (device: Device) => {
        const confirmed = confirmAction(
            `Vill du ta bort all data för "${device.name}" (${device.id})?\n\nAlla sparade mätvärden och inställningar raderas.\nSjälva enheten tas INTE bort.\n\nDet går inte att ångra.`
        );
        
        if (!confirmed) return;
        
        setError(null);
        try {
            await deleteDeviceData(device.id);
        } catch (err: any) {
            const errorMessage = err.message || 'Kunde inte ta bort enhetens data';
            setError(errorMessage);
            console.error('Remove device data error:', err);
        }
    };

    const handleToggleDisabled = async (device: Device) => {
        const newDisabled = !device.disabled;
        setError(null);
        try {
            await setDeviceDisabled(device.id, newDisabled);
            setDevices(prev => prev.map(d => d.id === device.id ? { ...d, disabled: newDisabled } : d));
        } catch (err: any) {
            setError(err.message || 'Kunde inte spara enheten');
        }
    };

    const formatLastSeen = (isoString?: string | null): string => {
        if (!isoString) return '—';
        const diff = Date.now() - new Date(isoString).getTime();
        if (diff < 60_000) return 'nyss';
        if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} min sedan`;
        if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} tim sedan`;
        return `${Math.floor(diff / 86_400_000)} dygn sedan`;
    };

    const uniqueTypes = ['all', ...Array.from(new Set(devices.map(d => d.type)))];
    const uniqueCoordinatorIds = Array.from(new Set(devices.map(d => d.coordinator_id).filter(Boolean))) as string[];
    const hasMultipleCoordinators = uniqueCoordinatorIds.length > 1;

    const filteredDevices = devices.filter(device => {
        const matchesText = filterText === '' ||
            device.name.toLowerCase().includes(filterText.toLowerCase()) ||
            device.id.toLowerCase().includes(filterText.toLowerCase());
        const matchesStatus = filterStatus === 'all' || device.status === filterStatus;
        const matchesType = filterType === 'all' || device.type === filterType;
        const matchesCoordinator = filterCoordinator === 'all' || device.coordinator_id === filterCoordinator;
        return matchesText && matchesStatus && matchesType && matchesCoordinator;
    });

    if (loading) {
        return <div style={{ padding: '20px' }}>Hämtar enheter…</div>;
    }

    return (
        <div>
            {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

            {/* ── Sub-tabs (mobile-friendly horizontal scroll) ───────── */}
            <div className="surveillance-tabs" style={{ gap: '4px' }}>
                {([
                    { key: 'list' as const,   label: `📱 Enheter (${devices.length})` },
                    { key: 'pair' as const,   label: pendingDevices.length > 0
                                                  ? `🔌 Parkoppla (${pendingDevices.length})`
                                                  : pairingMode ? `🔌 Parkopplar… ${pairingCountdown} s` : '🔌 Parkoppla' },
                    { key: 'manual' as const, label: '➕ Manuellt' },
                ].filter(t => !isReadOnly || t.key === 'list')).map(t => (
                    <button key={t.key} onClick={() => setViewTab(t.key)} style={{
                        padding: '10px 18px', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '14px',
                        backgroundColor: viewTab === t.key ? '#0f3460' : 'transparent',
                        color: viewTab === t.key ? 'white' : '#555',
                        borderRadius: '8px 8px 0 0',
                        whiteSpace: 'nowrap',
                        transition: 'all .15s',
                    }}>
                        {t.label}
                    </button>
                ))}
            </div>

            {/* ═════════ PAIR TAB ═════════ */}
            {viewTab === 'pair' && !isReadOnly && (
                <PairTab
                    pairingMode={pairingMode}
                    pairingCountdown={pairingCountdown}
                    coordinatorConnected={coordinatorConnected}
                    zigbeeGateways={zigbeeGateways}
                    pairingCoordinatorId={pairingCoordinatorId}
                    pendingDevices={pendingDevices}
                    confirmForms={confirmForms}
                    replaceMode={replaceMode}
                    replaceTarget={replaceTarget}
                    devices={devices}
                    onPairingCoordinatorChange={setPairingCoordinatorId}
                    onEnablePairing={handleEnablePairing}
                    onStopPairing={handleStopPairing}
                    onConfirmDevice={handleConfirmDevice}
                    onDismissPending={handleDismissPending}
                    onReplaceDevice={handleReplaceDevice}
                    onConfirmFormChange={(ieee, field, value) => setConfirmForms(prev => ({ ...prev, [ieee]: { ...prev[ieee], [field]: value } }))}
                    onToggleReplaceMode={(ieee, on) => setReplaceMode(prev => ({ ...prev, [ieee]: on }))}
                    onReplaceTargetChange={(ieee, value) => setReplaceTarget(prev => ({ ...prev, [ieee]: value }))}
                />
            )}

            {/* ═════════ LIST TAB ═════════ */}
            {viewTab === 'list' && (
              <div style={{ paddingTop: '12px' }}>
            {hasMultipleCoordinators && (
                <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
                    {['all', ...uniqueCoordinatorIds].map(cid => (
                        <button key={cid}
                            onClick={() => setFilterCoordinator(cid)}
                            style={{
                                padding: '6px 14px', fontSize: '12px', cursor: 'pointer', borderRadius: '20px',
                                fontWeight: filterCoordinator === cid ? 'bold' : 'normal',
                                background: filterCoordinator === cid ? '#4a6cf7' : '#e9ecef',
                                color: filterCoordinator === cid ? 'white' : '#555',
                                border: filterCoordinator === cid ? '1px solid #4a6cf7' : '1px solid #dee2e6',
                            }}>
                            📡 {cid === 'all' ? `Alla samordnare (${devices.length})` : `${cid} (${devices.filter(d => d.coordinator_id === cid).length})`}
                        </button>
                    ))}
                </div>
            )}

            <h2 style={{ margin: '0 0 16px', fontSize: 'clamp(16px, 4vw, 22px)' }}>
                Anslutna enheter ({filteredDevices.length}{filteredDevices.length !== devices.length ? ` av ${devices.length}` : ''})
            </h2>

              </div>
            )}

            {/* ═════════ MANUAL TAB ═════════ */}
            {viewTab === 'manual' && !isReadOnly && (
                <ManualTab
                    newDevice={newDevice}
                    onChange={setNewDevice}
                    onSubmit={handleAddDevice}
                />
            )}

            {/* ═════════ LIST TAB: filter + device list ═════════ */}
            {viewTab === 'list' && (<>
            
            {/* ── Filter row ────────────────────────────────────── */}
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
                <input
                    type="text"
                    placeholder="Sök på namn eller ID…"
                    value={filterText}
                    onChange={e => setFilterText(e.target.value)}
                    style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', flex: 1, minWidth: '160px' }}
                />
                <select
                    value={filterStatus}
                    onChange={e => setFilterStatus(e.target.value as any)}
                    style={{ padding: '8px 10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                >
                    <option value="all">Alla lägen</option>
                    <option value="online">Uppkopplad</option>
                    <option value="offline">Frånkopplad</option>
                    <option value="error">Fel</option>
                </select>
                <select
                    value={filterType}
                    onChange={e => setFilterType(e.target.value)}
                    style={{ padding: '8px 10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                >
                    {uniqueTypes.map(t => (
                        <option key={t} value={t}>{t === 'all' ? 'Alla typer' : `${getDeviceIcon(t)} ${getDeviceTypeLabel(t)}`}</option>
                    ))}
                </select>
                {(filterText || filterStatus !== 'all' || filterType !== 'all' || filterCoordinator !== 'all') && (
                    <button
                        onClick={() => { setFilterText(''); setFilterStatus('all'); setFilterType('all'); setFilterCoordinator('all'); }}
                        style={{ padding: '8px 12px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}
                    >
                        ✕ Rensa
                    </button>
                )}
            </div>

            {/* ── Device list ───────────────────────────────────── */}
            <div style={{ border: '1px solid #ddd', borderRadius: '4px' }}>
                {devices.length === 0 ? (
                    <p style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                        Inga enheter anslutna. Gå till fliken Parkoppla för att lägga till Zigbee-enheter.
                    </p>
                ) : filteredDevices.length === 0 ? (
                    <p style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                        Inga enheter matchar filtret.
                    </p>
                ) : (
                  <>
                    {/* ── Mobile card view ────────────────────────── */}
                    <div className="device-cards-mobile">
                        {filteredDevices.map(device => (
                            <React.Fragment key={device.id}>
                            <div className="device-card" style={device.disabled ? { opacity: 0.5 } : undefined}>
                                <div className="device-card-header">
                                    <div className="device-card-title">
                                        <span style={{ fontSize: '20px' }}>{getDeviceIcon(device.type)}</span>
                                        <strong>{device.name}</strong>
                                        {device.disabled && (
                                            <span title="Enheten är avstängd — meddelanden ignoreras" style={{ fontSize: '12px', color: '#999', fontWeight: 'normal' }}>avstängd</span>
                                        )}
                                        {device.coordinator_offline && (
                                            <span title="Samordnaren är frånkopplad" style={{ fontSize: '14px' }}>⚠️</span>
                                        )}
                                    </div>
                                    <span style={{ color: getStatusColor(device.status), fontWeight: 'bold', fontSize: '13px' }}>
                                        ● {getStatusLabel(device.status)}
                                    </span>
                                </div>
                                <div className="device-card-meta">
                                    <span>{getDeviceTypeLabel(device.type)}</span>
                                    <span>· {formatLastSeen(device.last_seen)}</span>
                                    {device.linkquality !== undefined && (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                            · <SignalIcon lq={device.linkquality} />
                                        </span>
                                    )}
                                    {device.battery != null && (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                            · <BatteryIcon pct={Math.round(device.battery)} />
                                            {device.battery_type && (
                                                <span style={{ fontSize: '10px', color: '#888', fontFamily: 'monospace' }}>
                                                    {device.battery_type}
                                                </span>
                                            )}
                                        </span>
                                    )}
                                </div>
                                <div className="device-card-id">
                                    <code>{device.id}</code>
                                </div>
                                <div className="device-card-actions">
                                    {(['switch', 'power_meter', 'io_module', 'light', 'dimmer', 'thermostat'].includes(device.type)) && (
                                        <button onClick={() => handleDeviceClick(device.id)} className="device-card-btn primary">
                                            🎮 Styr
                                        </button>
                                    )}
                                    {/* Samordnaren sköts under Gateways, inga åtgärder här */}
                                    {!isReadOnly && device.type !== 'coordinator' && (
                                        <>
                                            <button onClick={() => handleEditDevice(device)} className="device-card-btn warn">Ändra</button>
                                            <button onClick={() => handleRemoveData(device)} className="device-card-btn warn">Rensa</button>
                                            <button onClick={() => handleToggleDisabled(device)} className="device-card-btn" style={{ backgroundColor: device.disabled ? '#28a745' : '#6c757d', color: 'white' }}>{device.disabled ? 'Slå på' : 'Stäng av'}</button>
                                            <button onClick={() => handleRemoveDevice(device)} className="device-card-btn danger">Ta bort</button>
                                        </>
                                    )}
                                </div>
                            </div>
                            {!isReadOnly && showEditForm && editingDevice?.id === device.id && (
                                <div style={{ marginBottom: '12px' }}>
                                    <EditDeviceForm
                                        device={editingDevice}
                                        onChange={setEditingDevice}
                                        onSubmit={handleUpdateDevice}
                                        onCancel={() => { setShowEditForm(false); setEditingDevice(null); }}
                                    />
                                </div>
                            )}
                            </React.Fragment>
                        ))}
                    </div>

                    {/* ── Desktop table view ──────────────────────── */}
                    <div className="device-table-scroll">
                    <table className="device-table-desktop" style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #ddd' }}>
                                <th style={{ padding: '12px', textAlign: 'left' }}>Enhetsnamn</th>
                                <th style={{ padding: '12px', textAlign: 'left' }}>Typ</th>
                                <th style={{ padding: '12px', textAlign: 'left' }}>Läge</th>
                                <th style={{ padding: '12px', textAlign: 'left' }}>Senast sedd</th>
                                <th style={{ padding: '12px', textAlign: 'left' }}>Signal</th>
                                <th style={{ padding: '12px', textAlign: 'left' }}>Batteri</th>
                                <th style={{ padding: '12px', textAlign: 'left' }}>Enhets-ID</th>
                                <th style={{ padding: '12px', textAlign: 'left' }}>Åtgärder</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredDevices.map(device => (
                                <React.Fragment key={device.id}>
                                <tr
                                    style={{ borderBottom: '1px solid #ddd', transition: 'background-color 0.2s', opacity: device.disabled ? 0.55 : 1 }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                >
                                    <td style={{ padding: '12px' }}>
                                        <strong>{device.name}</strong>
                                        {device.disabled && (
                                            <span title="Enheten är avstängd — meddelanden ignoreras" style={{ marginLeft: '6px', fontSize: '11px', color: '#999', fontWeight: 'normal' }}>avstängd</span>
                                        )}
                                        {device.coordinator_offline && (
                                            <span title="Samordnaren är frånkopplad — läget kan vara gammalt" style={{ marginLeft: '6px', fontSize: '14px', cursor: 'default' }}>⚠️</span>
                                        )}
                                    </td>
                                    <td style={{ padding: '12px' }}>
                                        <span style={{ fontSize: '16px' }}>{getDeviceIcon(device.type)} {getDeviceTypeLabel(device.type)}</span>
                                    </td>
                                    <td style={{ padding: '12px' }}>
                                        <span style={{ color: getStatusColor(device.status), fontWeight: 'bold' }}>● {getStatusLabel(device.status)}</span>
                                    </td>
                                    <td style={{ padding: '12px', color: '#666', fontSize: '13px' }}>{formatLastSeen(device.last_seen)}</td>
                                    <td style={{ padding: '12px' }}>
                                        {device.linkquality !== undefined
                                            ? <SignalIcon lq={device.linkquality} />
                                            : <span style={{ color: '#ccc', fontSize: '12px' }}>—</span>}
                                    </td>
                                    <td style={{ padding: '12px' }}>
                                        {device.battery != null
                                            ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                                <BatteryIcon pct={Math.round(device.battery)} />
                                                {device.battery_type && (
                                                    <span style={{ fontSize: '11px', color: '#888', fontFamily: 'monospace' }}>{device.battery_type}</span>
                                                )}
                                              </span>
                                            : <span style={{ color: '#ccc', fontSize: '12px' }}>—</span>}
                                    </td>
                                    <td style={{ padding: '12px' }}>
                                        <code style={{ fontSize: '12px', color: '#666' }}>{device.id}</code>
                                    </td>
                                    <td style={{ padding: '12px' }}>
                                        {device.type === 'coordinator' && (
                                            <span style={{ color: '#888', fontSize: '12px' }}>Hanteras under Gateways</span>
                                        )}
                                        {!isReadOnly && device.type !== 'coordinator' && (
                                            <>
                                                <button onClick={() => handleEditDevice(device)} style={{ padding: '6px 12px', backgroundColor: '#ffc107', color: '#000', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '8px' }}>Ändra</button>
                                                <button onClick={() => handleRemoveData(device)} style={{ padding: '6px 12px', backgroundColor: '#ff8c00', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '8px' }}>Rensa data</button>
                                                <button onClick={() => handleToggleDisabled(device)} style={{ padding: '6px 12px', backgroundColor: device.disabled ? '#28a745' : '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '8px' }}>{device.disabled ? 'Slå på' : 'Stäng av'}</button>
                                                <button onClick={() => handleRemoveDevice(device)} style={{ padding: '6px 12px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '8px' }}>Ta bort</button>
                                            </>
                                        )}
                                        {(['switch', 'power_meter', 'io_module', 'light', 'dimmer', 'thermostat'].includes(device.type)) && (
                                            <button onClick={() => handleDeviceClick(device.id)} style={{ padding: '6px 12px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Styr</button>
                                        )}
                                    </td>
                                </tr>
                                {!isReadOnly && showEditForm && editingDevice?.id === device.id && (
                                    <tr>
                                        <td colSpan={8} style={{ padding: 0, backgroundColor: '#fafbfc' }}>
                                            <div style={{ padding: '12px' }}>
                                                <EditDeviceForm
                                                    device={editingDevice}
                                                    onChange={setEditingDevice}
                                                    onSubmit={handleUpdateDevice}
                                                    onCancel={() => { setShowEditForm(false); setEditingDevice(null); }}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                    </div>
                  </>
                )}
            </div>
            </>)}

        </div>
    );
};

export default DeviceList;
